// Queries and mutations of publishing a document by link. Keys are
// written here only (`["me", "publish", ...]`: the person's own, cleared on
// sign out).
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  PublicationDatasetsDTO,
  PublicationDTO,
  PublicationPreviewDTO,
  PublicationStateDTO,
} from "@trameria/core/publishing-shared";
import { useApi } from "../app/context";
import type { Api } from "../data/api";

export const publishPrefix = ["me", "publish"] as const;
const statePrefix = [...publishPrefix, "state"] as const;
const previewPrefix = [...publishPrefix, "preview"] as const;
const datasetsPrefix = [...publishPrefix, "datasets"] as const;

export const publicationKey = (pageId: string) =>
  [...statePrefix, pageId] as const;

const path = (pageId: string) =>
  `/api/me/pages/${encodeURIComponent(pageId)}/publication`;

/** Whether the document is published, and whether this server can publish. */
export const publicationQuery = (api: Api, pageId: string) =>
  queryOptions({
    queryKey: publicationKey(pageId),
    queryFn: () => api<PublicationStateDTO>(path(pageId)),
  });

/** The page as it would be published. Drawn anew each time the dialog asks. */
export const previewQuery = (api: Api, pageId: string) =>
  queryOptions({
    queryKey: [...previewPrefix, pageId] as const,
    queryFn: () => api<PublicationPreviewDTO>(`${path(pageId)}/preview`),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

/**
 * The live publication's datasets and the ones the document reads that are
 * not published (asked only while the dialog shows a live publication).
 */
export const publicationDatasetsQuery = (api: Api, pageId: string) =>
  queryOptions({
    queryKey: [...datasetsPrefix, pageId] as const,
    queryFn: () => api<PublicationDatasetsDTO>(`${path(pageId)}/datasets`),
    staleTime: 0,
  });

export function usePublish(pageId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    // The server answers the live publication again while there is one, so a
    // retry makes no second one (no Idempotency-Key needed). `datasets`: the
    // ones shown with the preview.
    mutationFn: (datasets: readonly string[]) =>
      api<PublicationDTO>(path(pageId), {
        method: "POST",
        body: { datasets },
      }),
    onSuccess: (publication) =>
      qc.setQueryData<PublicationStateDTO>(publicationKey(pageId), (old) => ({
        available: old?.available ?? true,
        publication,
      })),
    onSettled: () => qc.invalidateQueries({ queryKey: publicationKey(pageId) }),
  });
}

/** Publishes more datasets with the live publication (the server answers the new lists). */
export function useAddDatasets(pageId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (add: readonly string[]) =>
      api<PublicationDatasetsDTO>(`${path(pageId)}/datasets`, {
        method: "POST",
        body: { add },
      }),
    onSuccess: (lists) => {
      qc.setQueryData([...datasetsPrefix, pageId], lists);
      qc.setQueryData<PublicationStateDTO>(publicationKey(pageId), (old) =>
        old?.publication
          ? {
              ...old,
              publication: {
                ...old.publication,
                datasetIds: lists.published.map((d) => d.id),
              },
            }
          : old,
      );
    },
    onSettled: () => qc.invalidateQueries({ queryKey: publicationKey(pageId) }),
  });
}

export function useUnpublish(pageId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>(path(pageId), { method: "DELETE" }),
    onSuccess: () =>
      qc.setQueryData<PublicationStateDTO>(publicationKey(pageId), (old) =>
        old ? { ...old, publication: null } : old,
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: publicationKey(pageId) }),
  });
}
